// app/components/WhyUsSectionClient.tsx
'use client';

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PortableTextBlock } from '@portabletext/types';
import { m, useScroll, useTransform, type MotionValue, LazyMotion, domAnimation } from 'framer-motion';
import Image, { type ImageProps } from 'next/image';
import { PortableTextBody } from '@/components/PortableTextBody';
import ButtonLandingPage from '@/components/landing/ButtonLandingPage';
import clsx from 'clsx';

// --- CONFIG ---
const DESKTOP_PREFETCH_ALL = true;
const RETRY_LIMIT = 2;
const FALLBACK_SRC = '/33.webp';

// iOS/WebKit: border-radius + overflow-hidden potrafi nie przycinać dzieci (stare iPhone).
const IOS_CLIP_FIX =
    '[transform:translateZ(0)] [backface-visibility:hidden] [will-change:transform] ' +
    '[-webkit-mask-image:-webkit-radial-gradient(white,black)] [mask-image:radial-gradient(white,black)]';

// --- TYPES & CONSTANTS ---
const slideUp = {
    hidden: { y: 30, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

type ClientImage = { src: string; alt?: string; blur?: string };
type ClientBlock = { heading: string; description: string; image: ClientImage };
type ClientProps = { heading: string; body: PortableTextBlock[]; contentBlocks: ClientBlock[] };

const WHY_US_LINK_CLASS = 'text-primary underline underline-offset-4 hover:text-accent';

// --- HELPERS ---
function withCacheBust(url: string, key: string) {
    const hasQuery = url.includes('?');
    return `${url}${hasQuery ? '&' : '?'}${key}=${Date.now()}`;
}

function sanityJpgUrl(url: string, w: number, q = 75) {
    const u = new URL(url);
    u.searchParams.set('w', String(w));
    u.searchParams.set('q', String(q));
    u.searchParams.set('fm', 'jpg');
    u.searchParams.delete('auto');
    return u.toString();
}

function usePrefetchImages(urls: string[]) {
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const uniq = Array.from(new Set(urls.filter(Boolean)));
        for (const src of uniq) {
            const img = new window.Image();
            img.decoding = 'async';
            img.loading = 'eager';
            img.src = src;
        }
    }, [urls.join('|')]);
}

// --- SVG LINE ---
const LinePath = memo(function LinePath({
                                            className,
                                            scrollYProgress,
                                        }: {
    className: string;
    scrollYProgress: MotionValue<number>;
}) {
    const pathLength = useTransform(scrollYProgress, [0, 1], [0.5, 1]);
    const dashOffset = useTransform(pathLength, (value) => 1 - value);

    return (
        <svg
            width="1278"
            height="2319"
            viewBox="0 0 1278 2319"
            fill="none"
            overflow="visible"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
            aria-hidden="true"
        >
            <m.path
                d="M876.605 394.131C788.982 335.917 696.198 358.139 691.836 416.303C685.453 501.424 853.722 498.43 941.95 409.714C1016.1 335.156 1008.64 186.907 906.167 142.846C807.014 100.212 712.699 198.494 789.049 245.127C889.053 306.207 986.062 116.979 840.548 43.3233C743.932 -5.58141 678.027 57.1682 672.279 112.188C666.53 167.208 712.538 172.943 736.353 163.088C760.167 153.234 764.14 120.924 746.651 93.3868C717.461 47.4252 638.894 77.8642 601.018 116.979C568.164 150.908 557 201.079 576.467 246.924C593.342 286.664 630.24 310.55 671.68 302.614C756.114 286.446 729.747 206.546 681.86 186.442C630.54 164.898 492 209.318 495.026 287.644C496.837 334.494 518.402 366.466 582.455 367.287C680.013 368.538 771.538 299.456 898.634 292.434C1007.02 286.446 1192.67 309.384 1242.36 382.258C1266.99 418.39 1273.65 443.108 1247.75 474.477C1217.32 511.33 1149.4 511.259 1096.84 466.093C1044.29 420.928 1029.14 380.576 1033.97 324.172C1038.31 273.428 1069.55 228.986 1117.2 216.384C1152.2 207.128 1188.29 213.629 1194.45 245.127C1201.49 281.062 1132.22 280.104 1100.44 272.673C1065.32 264.464 1044.22 234.837 1032.77 201.413C1019.29 162.061 1029.71 131.126 1056.44 100.965C1086.19 67.4032 1143.96 54.5526 1175.78 86.1513C1207.02 117.17 1186.81 143.379 1156.22 166.691C1112.57 199.959 1052.57 186.238 999.784 155.164C957.312 130.164 899.171 63.7054 931.284 26.3214C952.068 2.12513 996.288 3.87363 1007.22 43.58C1018.15 83.2749 1003.56 122.644 975.969 163.376C948.377 204.107 907.272 255.122 913.558 321.045C919.727 385.734 990.968 497.068 1063.84 503.35C1111.46 507.456 1166.79 511.984 1175.68 464.527C1191.52 379.956 1101.26 334.985 1030.29 377.017C971.109 412.064 956.297 483.647 953.797 561.655C947.587 755.413 1197.56 941.828 936.039 1140.66C745.771 1285.32 321.926 950.737 134.536 1202.19C-6.68295 1391.68 -53.4837 1655.38 131.935 1760.5C478.381 1956.91 1124.19 1515 1201.28 1997.83C1273.66 2451.23 100.805 1864.7 303.794 2668.89"
                stroke="#224737"
                strokeWidth="26"
                strokeOpacity={0.45}
                style={{ pathLength, strokeDashoffset: dashOffset }}
            />
        </svg>
    );
});

type SafeNextImageProps = {
    src: string;
    alt: string;
    fill?: boolean;
    sizes: string;
    className?: string;
    priority?: boolean;
    loading?: ImageProps['loading'];
    decoding?: ImageProps['decoding'];
    placeholder?: ImageProps['placeholder'];
    blurDataURL?: string;
    unoptimized?: boolean;
};

function SafeNextImage(props: SafeNextImageProps) {
    const [attempt, setAttempt] = useState(0);
    const [failed, setFailed] = useState(false);

    const effectiveSrc = useMemo(() => {
        if (attempt === 0) return props.src;
        return withCacheBust(props.src, '_retry');
    }, [props.src, attempt]);

    if (failed) {
        return (
            <div className="absolute inset-0">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    src={FALLBACK_SRC}
                    alt={props.alt}
                    className={clsx('absolute inset-0 h-full w-full object-cover', props.className)}
                    loading="eager"
                    decoding="async"
                />
            </div>
        );
    }

    return (
        <Image
            key={effectiveSrc}
            src={effectiveSrc}
            alt={props.alt}
            fill={props.fill}
            sizes={props.sizes}
            className={props.className}
            priority={props.priority}
            loading={props.loading}
            decoding={props.decoding ?? 'async'}
            placeholder={props.placeholder}
            blurDataURL={props.blurDataURL}
            unoptimized={props.unoptimized}
            onError={() => {
                if (attempt < RETRY_LIMIT) setAttempt((p) => p + 1);
                else setFailed(true);
            }}
        />
    );
}

// --- 1) MOBILE (<768) ---
const MobileLayout = ({
                          contentBlocks,
                          heading,
                          body,
                      }: {
    contentBlocks: ClientBlock[];
    heading: string;
    body: PortableTextBlock[];
}) => {
    return (
        <div className="relative z-10 block pt-16 px-[5%] [@media(min-width:768px)]:hidden">
            <div className="mx-auto max-w-lg text-center mb-16">
                <m.h2 variants={slideUp} custom={0} className="mb-5 text-3xl font-medium">
                    {heading}
                </m.h2>
                {body.length > 0 ? (
                    <m.div variants={slideUp} custom={1} className="max-w-md mx-auto">
                        <PortableTextBody
                            value={body}
                            paragraphClassName="text-base text-primary"
                            linkClassName={WHY_US_LINK_CLASS}
                        />
                    </m.div>
                ) : null}
                <m.div variants={slideUp} custom={2} className="mt-10 flex justify-center">
                    <ButtonLandingPage label="Napisz do nas" />
                </m.div>
            </div>

            <div className="flex flex-col gap-16">
                {contentBlocks.map((block, index) => {
                    const isLcp = index === 0;
                    const safeSrc = block.image.src ? sanityJpgUrl(block.image.src, 1000, 75) : '';

                    return (
                        <div key={index} className="flex flex-col">
                            <div
                                className={clsx(
                                    'relative mb-6 aspect-[3/5] w-full overflow-hidden rounded-3xl bg-secondary-light/20',
                                    IOS_CLIP_FIX
                                )}
                            >
                                {block.image.src ? (
                                    <SafeNextImage
                                        src={safeSrc}
                                        alt={block.image.alt || ''}
                                        fill
                                        className="object-cover object-top"
                                        sizes="100vw"
                                        priority={isLcp}
                                        loading="eager"
                                        decoding="async"
                                        placeholder={block.image.blur ? 'blur' : 'empty'}
                                        blurDataURL={block.image.blur}
                                        unoptimized
                                    />
                                ) : (
                                    <div className="absolute inset-0">
                                        {/* eslint-disable-next-line @next/next/no-img-element */}
                                        <img
                                            src={FALLBACK_SRC}
                                            alt=""
                                            className="absolute inset-0 h-full w-full object-cover"
                                            loading="eager"
                                            decoding="async"
                                        />
                                    </div>
                                )}
                            </div>

                            <m.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, margin: '-10%' }}
                                variants={slideUp}
                                custom={0}
                            >
                                <h3 className="mb-4 text-3xl font-medium leading-tight">{block.heading}</h3>
                                <p className="text-base opacity-90">{block.description}</p>
                            </m.div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --- 2) TABLET (>=768 + touch) ---
const TabletLayout = ({
                          contentBlocks,
                          heading,
                          body,
                      }: {
    contentBlocks: ClientBlock[];
    heading: string;
    body: PortableTextBlock[];
}) => {
    return (
        <div
            className={clsx(
                'relative z-10 hidden pt-20 pb-20 px-[5%]',
                '[@media(min-width:768px)_and_(pointer:coarse)]:block'
            )}
        >
            <div className="mx-auto max-w-4xl text-center mb-16">
                <m.h2 variants={slideUp} custom={0} className="mb-5 text-4xl font-medium">
                    {heading}
                </m.h2>
                {body.length > 0 ? (
                    <m.div variants={slideUp} custom={1} className="max-w-[52ch] mx-auto">
                        <PortableTextBody
                            value={body}
                            paragraphClassName="text-base text-primary"
                            linkClassName={WHY_US_LINK_CLASS}
                        />
                    </m.div>
                ) : null}
                <m.div variants={slideUp} custom={2} className="mt-10 flex justify-center">
                    <ButtonLandingPage label="Napisz do nas" />
                </m.div>
            </div>

            <div className="flex flex-col gap-14">
                {contentBlocks.map((block, index) => {
                    const isLcp = index === 0;
                    const safeSrc = block.image.src ? sanityJpgUrl(block.image.src, 1100, 75) : '';

                    return (
                        <div key={index} className="grid grid-cols-2 gap-10 items-center">
                            <div
                                className={clsx(
                                    'relative aspect-[3/4] w-full overflow-hidden rounded-3xl bg-secondary-light/20',
                                    IOS_CLIP_FIX
                                )}
                            >
                                {block.image.src ? (
                                    <SafeNextImage
                                        src={safeSrc}
                                        alt={block.image.alt || ''}
                                        fill
                                        className="object-cover object-top"
                                        sizes="50vw"
                                        priority={isLcp}
                                        loading="eager"
                                        decoding="async"
                                        placeholder={block.image.blur ? 'blur' : 'empty'}
                                        blurDataURL={block.image.blur}
                                        unoptimized
                                    />
                                ) : (
                                    <div className="absolute inset-0">
                                        {/* eslint-disable-next-line @next/next/no-img-element */}
                                        <img
                                            src={FALLBACK_SRC}
                                            alt=""
                                            className="absolute inset-0 h-full w-full object-cover"
                                            loading="eager"
                                            decoding="async"
                                        />
                                    </div>
                                )}
                            </div>

                            <m.div
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, margin: '-10%' }}
                                variants={slideUp}
                                custom={0}
                            >
                                <h3 className="mb-4 text-4xl font-medium leading-tight">{block.heading}</h3>
                                <p className="text-base opacity-90">{block.description}</p>
                            </m.div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// --- 3) DESKTOP (>=768 i NIE touch) ---
const DesktopLayout = ({
                           contentBlocks,
                           heading,
                           body,
                       }: {
    contentBlocks: ClientBlock[];
    heading: string;
    body: PortableTextBlock[];
}) => {
    const sectionRef = useRef<HTMLElement>(null);
    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start 90%', 'end 10%'] });

    const [activeIndex, setActiveIndex] = useState(0);
    const activeRef = useRef(0);
    const rafRef = useRef<number | null>(null);
    const pendingRef = useRef<number | null>(null);

    useEffect(() => {
        activeRef.current = activeIndex;
    }, [activeIndex]);

    const setActiveIndexOptimized = useCallback((next: number) => {
        if (next === activeRef.current) return;
        pendingRef.current = next;
        if (rafRef.current != null) return;

        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            const v = pendingRef.current;
            pendingRef.current = null;
            if (typeof v === 'number' && v !== activeRef.current) setActiveIndex(v);
        });
    }, []);

    const handleVisibilityChange = useCallback(
        (index: number, isVisible: boolean) => {
            if (isVisible) setActiveIndexOptimized(index);
        },
        [setActiveIndexOptimized]
    );

    const safeActive = Math.min(Math.max(activeIndex, 0), Math.max(contentBlocks.length - 1, 0));

    const desktopUrls = useMemo(() => {
        const urls = contentBlocks.map((b) => b.image?.src).filter(Boolean) as string[];
        return urls.map((u) => sanityJpgUrl(u, 1400, 80));
    }, [contentBlocks]);

    const prefetchUrls = useMemo(() => {
        if (!DESKTOP_PREFETCH_ALL) return [];
        return desktopUrls;
    }, [desktopUrls]);

    usePrefetchImages(prefetchUrls);

    return (
        <section
            ref={sectionRef}
            className={clsx(
                'relative z-10 hidden pt-24 lg:pt-28 px-[5%] overflow-x-clip',
                '[@media(min-width:768px)]:block', // ✅ desktop/tablet bazowo pokazuj
                '[@media(min-width:768px)_and_(pointer:coarse)]:hidden' // ✅ ale ukryj na touch (tablet)
            )}
        >
            <div className="pointer-events-none absolute inset-y-0 right-0 z-0 overflow-visible">
                <LinePath
                    scrollYProgress={scrollYProgress}
                    className="absolute top-[clamp(-7rem,1.5vw,0.5rem)] right-0 h-auto origin-top-right scale-[1.14] w-[clamp(1250px,86vw,2300px)] translate-x-[clamp(30px,5vw,120px)]"
                />
            </div>

            <div className="container relative z-10">
                <div className="mx-auto max-w-lg text-center relative z-10">
                    <m.h2 variants={slideUp} custom={0} className="mb-5 text-3xl font-medium md:mb-6 md:text-7xl lg:text-8xl">
                        {heading}
                    </m.h2>
                    {body.length > 0 ? (
                        <m.div variants={slideUp} custom={1} className="max-w-[43rem] mx-auto">
                            <PortableTextBody
                                value={body}
                                paragraphClassName="text-base text-primary md:text-md"
                                linkClassName={WHY_US_LINK_CLASS}
                            />
                        </m.div>
                    ) : null}
                    <m.div variants={slideUp} custom={2} className="mt-10 flex justify-center">
                        <ButtonLandingPage label="Napisz do nas" />
                    </m.div>
                </div>

                <div className="mt-12 grid grid-cols-1 items-start gap-12 md:mt-0 md:grid-cols-3 md:gap-x-8 md:gap-y-16 lg:gap-x-12 relative z-10">
                    <div className="relative flex w-full flex-col gap-12 md:pt-[60vh]">
                        {contentBlocks.map((block, index) =>
                            index % 2 === 0 ? (
                                <div key={index} className="md:h-svh">
                                    <DesktopContentBlock {...block} index={index} onVisibilityChange={(v) => handleVisibilityChange(index, v)} />
                                </div>
                            ) : null
                        )}
                    </div>

                    <div className="sticky top-0 hidden h-screen w-full items-center justify-center md:flex rounded-3xl">
                        <div
                            className={clsx(
                                'relative aspect-[3/4] w-full rounded-3xl overflow-hidden border-4 border-primary bg-secondary-light/20',
                                IOS_CLIP_FIX
                            )}
                        >
                            {contentBlocks.map((block, index) => {
                                const src = block.image.src ? sanityJpgUrl(block.image.src, 1400, 80) : '';

                                return (
                                    <div
                                        key={index}
                                        className={clsx(
                                            'absolute inset-0 transition-opacity duration-500 ease-in-out',
                                            index === safeActive ? 'opacity-100 z-10' : 'opacity-0 z-0'
                                        )}
                                    >
                                        {block.image.src ? (
                                            <SafeNextImage
                                                src={src}
                                                alt={block.image.alt || ''}
                                                fill
                                                className="object-cover object-top"
                                                sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                                                priority={index === 0}
                                                loading="eager"
                                                decoding="async"
                                                placeholder={block.image.blur ? 'blur' : 'empty'}
                                                blurDataURL={block.image.blur}
                                                unoptimized
                                            />
                                        ) : (
                                            <div className="absolute inset-0">
                                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                                <img
                                                    src={FALLBACK_SRC}
                                                    alt=""
                                                    className="absolute inset-0 h-full w-full object-cover"
                                                    loading="eager"
                                                    decoding="async"
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="relative hidden w-full flex-col gap-12 md:flex md:pt-[110vh]">
                        {contentBlocks.map((block, index) =>
                            index % 2 !== 0 ? (
                                <div key={index} className="md:h-svh">
                                    <DesktopContentBlock {...block} index={index} onVisibilityChange={(v) => handleVisibilityChange(index, v)} />
                                </div>
                            ) : null
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

const DesktopContentBlock = memo(function DesktopContentBlock(
    props: ClientBlock & { index: number; onVisibilityChange: (isVisible: boolean) => void }
) {
    const blockRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({ target: blockRef, offset: ['start end', 'end start'] });
    const opacity = useTransform(scrollYProgress, [0, 0.33, 0.5, 0.66, 0.83], [0, 0, 1, 1, 0]);

    useEffect(() => {
        let last = false;
        const unsub = opacity.on('change', (v) => {
            const visible = v > 0.12;
            if (visible === last) return;
            last = visible;
            props.onVisibilityChange(visible);
        });
        return () => unsub();
    }, [opacity, props.onVisibilityChange]);

    return (
        <m.div ref={blockRef} style={{ opacity }} className="flex flex-col items-start justify-center md:justify-start rounded-3xl">
            <h3 className="mb-2 text-2xl font-medium md:mb-4 md:text-6xl">{props.heading}</h3>
            <p className="md:text-md mb-8">{props.description}</p>
        </m.div>
    );
});

// --- MAIN ---
export default function WhyUsSectionClient(props: ClientProps) {
    return (
        <LazyMotion features={domAnimation}>
            <div className="relative bg-secondary text-primary">
                <MobileLayout {...props} />
                <TabletLayout {...props} />
                <DesktopLayout {...props} />
            </div>
        </LazyMotion>
    );
}